import React from 'react'
import classNames from 'classnames/bind'
import { router } from '@inertiajs/react'
import { useAdminAuth } from '@/admin/shared/hooks/useAdminPageProps'
import { adminNavigation } from '@/admin/app/adminNavigation'
import styles from './AdminSidebar.module.scss'

const cx = classNames.bind(styles)

// ── Brand SVG ─────────────────────────────────────────────────────────────────
const BrandLogo = () => (
  <svg viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
    <path fill="#ffffff" d="M14.747 9.125c.527-1.426 1.736-2.573 3.317-2.573c1.643 0 2.792 1.085 3.318 2.573l6.077 16.867c.186.496.248.931.248 1.147c0 1.209-.992 2.046-2.139 2.046c-1.303 0-1.954-.682-2.264-1.611l-.931-2.915h-8.62l-.93 2.884c-.31.961-.961 1.642-2.232 1.642c-1.24 0-2.294-.93-2.294-2.17c0-.496.155-.868.217-1.023l6.233-16.867zm.34 11.256h5.891l-2.883-8.992h-.062l-2.946 8.992z"/>
  </svg>
)

// ── Props ─────────────────────────────────────────────────────────────────────
interface AdminSidebarProps {
  currentPath?: string
  drawerOpen?: boolean
  onDrawerClose?: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────
function AdminSidebar({ currentPath = '', drawerOpen = false, onDrawerClose }: AdminSidebarProps) {
  const { user } = useAdminAuth()
  const userName = user?.name ?? 'Admin'
  const userEmail = user?.email ?? ''
  const initials = userName
    .split(' ')
    .map((w: string) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const isActive = (href: string) => {
    if (href === '/admin') return currentPath === '/admin' || currentPath === '/admin/'
    return currentPath.startsWith(href)
  }

  const handleNav = (href: string) => {
    onDrawerClose?.()
    router.visit(href)
  }

  return (
    <aside
      className={cx('sidebar', { sidebarDrawerOpen: drawerOpen })}
      aria-label="Main navigation"
    >
      {/* Brand */}
      <div className={cx('brand')}>
        <div className={cx('brandLogo')}><BrandLogo /></div>
        <div className={cx('brandText')}>
          <div className={cx('brandName')}>MemoSlate</div>
          <div className={cx('brandTag')}>admin panel</div>
        </div>
      </div>

      {/* Nav sections */}
      {adminNavigation.map(section => (
        <nav key={section.label} className={cx('navSection')}>
          <p className={cx('navLabel')}>{section.label}</p>
          {section.items.map(item => (
            <button
              key={item.key}
              className={cx('navLink', { navLinkActive: isActive(item.href) })}
              onClick={() => handleNav(item.href)}
            >
              {item.icon}
              <span>{item.text}</span>
            </button>
          ))}
        </nav>
      ))}

      {/* Sidebar footer */}
      <div className={cx('sidebarFooter')}>
        <div className={cx('workspace')}>
          <div className={cx('workspaceAvatar')}>{initials}</div>
          <div className={cx('workspaceText')}>
            <div className={cx('workspaceName')}>{userName}</div>
            {userEmail && <div className={cx('workspaceEmail')}>{userEmail}</div>}
          </div>
        </div>
      </div>
    </aside>
  )
}

export default AdminSidebar
